import { proxy } from "valtio";
import {
  type AdminApp,
  type AdminOrg,
  type AdminScope,
  type BugReport,
  type FeatureFlag,
  db,
} from "@/lib/instantdb";

// Re-export types with resolved relations for backward compatibility
export interface User {
  id: string;
  authId: string; // Alias for id for compatibility
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  roles?: Role[]; // Resolved roles
  createdAt: number;
  updatedAt: number;
}

export interface Role {
  id: string;
  name: string;
  description: string;
  scopes?: Scope[]; // Resolved scopes
  createdAt: number;
  updatedAt: number;
}

export type App = AdminApp;
export type Org = AdminOrg;
export type Scope = AdminScope;
export type { BugReport, FeatureFlag };

interface AdminStore {
  // Dashboard stats
  stats: {
    usersCount: number;
    appsCount: number;
    rolesCount: number;
    orgsCount: number;
  };

  // Users
  users: User[];
  usersTotal: number;
  usersPage: number;
  usersSearch: string | undefined;
  usersLoading: boolean;

  // Roles
  roles: Role[];
  rolesLoading: boolean;

  // Apps
  apps: App[];
  appsLoading: boolean;

  // Orgs
  orgs: Org[];
  orgsLoading: boolean;

  // Scopes
  scopes: Scope[];
  scopesLoading: boolean;

  // Bug Reports
  bugReports: BugReport[];
  bugReportsLoading: boolean;
  bugReportsFetched: boolean;

  // Feature Flags
  featureFlags: FeatureFlag[];
  featureFlagsLoading: boolean;
  featureFlagsFetched: boolean;

  // Error state
  error: string | null;
  loading: boolean;
}

export const adminStore = proxy<AdminStore>({
  stats: {
    usersCount: 0,
    appsCount: 0,
    rolesCount: 0,
    orgsCount: 0,
  },
  users: [],
  usersTotal: 0,
  usersPage: 1,
  usersSearch: undefined,
  usersLoading: false,
  roles: [],
  rolesLoading: false,
  apps: [],
  appsLoading: false,
  orgs: [],
  orgsLoading: false,
  scopes: [],
  scopesLoading: false,
  bugReports: [],
  bugReportsLoading: false,
  bugReportsFetched: false,
  featureFlags: [],
  featureFlagsLoading: false,
  featureFlagsFetched: false,
  error: null,
  loading: false,
});

// ========== Dashboard ==========

export async function fetchDashboardStats(force = false) {
  // Cache-first: skip fetch if we already have stats data
  if (!force && adminStore.stats.usersCount > 0) return;

  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      admin_users: {},
      admin_apps: {},
      admin_roles: {},
      admin_orgs: {},
    });

    adminStore.stats = {
      usersCount: data?.admin_users?.length ?? 0,
      appsCount: data?.admin_apps?.length ?? 0,
      rolesCount: data?.admin_roles?.length ?? 0,
      orgsCount: data?.admin_orgs?.length ?? 0,
    };
  } catch (e) {
    adminStore.error =
      e instanceof Error ? e.message : "Failed to fetch dashboard stats";
  } finally {
    adminStore.loading = false;
  }
}

// ========== Users ==========

export async function fetchUsers(
  page = 1,
  pageSize = 10,
  search?: string,
  force = false,
) {
  // Cache-first: skip fetch if same page/search request and data exists
  const isSameRequest =
    page === adminStore.usersPage && search === adminStore.usersSearch;
  if (!force && isSameRequest && adminStore.users.length > 0) return;

  adminStore.usersLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      admin_users: {},
      admin_roles: {},
      admin_scopes: {},
    });

    type RawUser = {
      id: string;
      email: string;
      name: string;
      firstName?: string;
      lastName?: string;
      roles: unknown; // JSON field from InstantDB
      createdAt: number;
      updatedAt: number;
    };
    type RawRole = {
      id: string;
      name: string;
      description: string;
      scopes: unknown; // JSON field from InstantDB
      createdAt: number;
      updatedAt: number;
    };
    type RawScope = { id: string; name: string; description: string };

    let users = (data?.admin_users ?? []) as RawUser[];
    const roles = (data?.admin_roles ?? []) as RawRole[];
    const scopes = (data?.admin_scopes ?? []) as RawScope[];

    // Client-side search filter
    if (search) {
      const s = search.toLowerCase();
      users = users.filter(
        (u) =>
          u.email?.toLowerCase().includes(s) ||
          u.name?.toLowerCase().includes(s) ||
          u.firstName?.toLowerCase().includes(s) ||
          u.lastName?.toLowerCase().includes(s),
      );
    }

    // Map roles with their scopes
    const rolesWithScopes: Role[] = roles.map((r) => {
      const roleScopes = (r.scopes as string[]) ?? [];
      return {
        id: r.id,
        name: r.name,
        description: r.description,
        scopes: scopes.filter((s) => roleScopes.includes(s.id)),
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      };
    });

    // Map users with their roles
    const usersWithRoles: User[] = users.map((u) => {
      const userRoles = (u.roles as string[]) ?? [];
      return {
        id: u.id,
        authId: u.id,
        email: u.email,
        name: u.name,
        firstName: u.firstName,
        lastName: u.lastName,
        roles: rolesWithScopes.filter((r) => userRoles.includes(r.id)),
        createdAt: u.createdAt,
        updatedAt: u.updatedAt,
      };
    });

    // Client-side pagination
    const total = usersWithRoles.length;
    const start = (page - 1) * pageSize;
    const paged = usersWithRoles.slice(start, start + pageSize);

    adminStore.users = paged;
    adminStore.usersTotal = total;
    adminStore.usersPage = page;
    adminStore.usersSearch = search;
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to fetch users";
  } finally {
    adminStore.usersLoading = false;
  }
}

// ========== Roles ==========

export async function fetchRoles(force = false) {
  // Cache-first: skip fetch if we already have roles
  if (!force && adminStore.roles.length > 0) return;

  adminStore.rolesLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      admin_roles: {},
      admin_scopes: {},
    });

    type RawRole = {
      id: string;
      name: string;
      description: string;
      scopes: unknown; // JSON field from InstantDB
      createdAt: number;
      updatedAt: number;
    };
    type RawScope = { id: string; name: string; description: string };

    const roles = (data?.admin_roles ?? []) as RawRole[];
    const scopes = (data?.admin_scopes ?? []) as RawScope[];

    // Map roles with their scopes
    const rolesWithScopes: Role[] = roles.map((r) => {
      const roleScopes = (r.scopes as string[]) ?? [];
      return {
        id: r.id,
        name: r.name,
        description: r.description,
        scopes: scopes.filter((s) => roleScopes.includes(s.id)),
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      };
    });

    adminStore.roles = rolesWithScopes;
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to fetch roles";
  } finally {
    adminStore.rolesLoading = false;
  }
}

export async function createRole(role: Partial<Role>) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { createAdminRole } = await import("@/lib/instantdb");
    const roleId = await createAdminRole({
      name: role.name ?? "",
      description: role.description ?? "",
      scopes: role.scopes?.map((s) => s.id) ?? [],
    });

    // Refresh roles (force to get the new role)
    await fetchRoles(true);

    return adminStore.roles.find((r) => r.id === roleId);
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to create role";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

export async function updateRole(id: string, role: Partial<Role>) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { updateAdminRole } = await import("@/lib/instantdb");
    await updateAdminRole(id, {
      name: role.name,
      description: role.description,
      scopes: role.scopes?.map((s) => s.id),
    });

    // Refresh roles (force to get the updated role)
    await fetchRoles(true);

    return adminStore.roles.find((r) => r.id === id);
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to update role";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

export async function deleteRole(id: string) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { deleteAdminRole } = await import("@/lib/instantdb");
    await deleteAdminRole(id);

    adminStore.roles = adminStore.roles.filter((r) => r.id !== id);
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to delete role";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

// ========== Apps ==========

export async function fetchApps(force = false) {
  // Cache-first: skip fetch if we already have apps
  if (!force && adminStore.apps.length > 0) return;

  adminStore.appsLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      admin_apps: {},
    });

    adminStore.apps = (data?.admin_apps ?? []) as App[];
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to fetch apps";
  } finally {
    adminStore.appsLoading = false;
  }
}

export async function createApp(
  app: Partial<
    Omit<App, "id" | "clientId" | "clientSecret" | "createdAt" | "updatedAt">
  >,
) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { createAdminApp } = await import("@/lib/instantdb");
    // Generate client ID and secret
    const clientId = `vizi_${crypto.randomUUID().replace(/-/g, "").slice(0, 24)}`;
    const clientSecret =
      crypto.randomUUID().replace(/-/g, "") +
      crypto.randomUUID().replace(/-/g, "");

    const appId = await createAdminApp({
      name: app.name ?? "",
      clientId,
      clientSecret,
      redirectUris: app.redirectUris ?? [],
    });

    // Refresh apps (force to get the new app)
    await fetchApps(true);

    return adminStore.apps.find((a) => a.id === appId);
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to create app";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

export async function updateApp(
  id: string,
  app: Partial<Omit<App, "id" | "clientId" | "clientSecret" | "createdAt">>,
) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { updateAdminApp } = await import("@/lib/instantdb");
    await updateAdminApp(id, {
      name: app.name,
      redirectUris: app.redirectUris,
    });

    // Refresh apps (force to get the updated app)
    await fetchApps(true);

    return adminStore.apps.find((a) => a.id === id);
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to update app";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

export async function deleteApp(id: string) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { deleteAdminApp } = await import("@/lib/instantdb");
    await deleteAdminApp(id);

    adminStore.apps = adminStore.apps.filter((a) => a.id !== id);
  } catch (e) {
    adminStore.error = e instanceof Error ? e.message : "Failed to delete app";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

export async function regenerateAppSecret(id: string) {
  adminStore.loading = true;
  adminStore.error = null;

  try {
    const { updateAdminApp } = await import("@/lib/instantdb");
    const newSecret =
      crypto.randomUUID().replace(/-/g, "") +
      crypto.randomUUID().replace(/-/g, "");

    await updateAdminApp(id, {
      clientSecret: newSecret,
    });

    // Refresh apps (force to get the updated secret)
    await fetchApps(true);

    return newSecret;
  } catch (e) {
    adminStore.error =
      e instanceof Error ? e.message : "Failed to regenerate secret";
    throw e;
  } finally {
    adminStore.loading = false;
  }
}

// ========== Orgs ==========

export async function fetchOrgs(force = false) {
  // Cache-first: skip fetch if we already have orgs
  if (!force && adminStore.orgs.length > 0) return;

  adminStore.orgsLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      admin_orgs: {},
    });

    adminStore.orgs = (data?.admin_orgs ?? []) as Org[];
  } catch (e) {
    adminStore.error =
      e instanceof Error ? e.message : "Failed to fetch organizations";
  } finally {
    adminStore.orgsLoading = false;
  }
}

// ========== Scopes ==========

export async function fetchScopes(force = false) {
  // Cache-first: skip fetch if we already have scopes
  if (!force && adminStore.scopes.length > 0) return;

  adminStore.scopesLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      admin_scopes: {},
    });

    adminStore.scopes = (data?.admin_scopes ?? []) as Scope[];
  } catch (e) {
    adminStore.error =
      e instanceof Error ? e.message : "Failed to fetch scopes";
  } finally {
    adminStore.scopesLoading = false;
  }
}

// ========== Bug Reports ==========

export async function fetchBugReports(force = false) {
  // Cache-first: skip if already fetched (even if empty)
  if (!force && adminStore.bugReportsFetched) return;

  adminStore.bugReportsLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      bug_reports: {},
    });

    adminStore.bugReports = (data?.bug_reports ?? []) as BugReport[];
    adminStore.bugReportsFetched = true;
  } catch (e) {
    adminStore.error =
      e instanceof Error ? e.message : "Failed to fetch bug reports";
  } finally {
    adminStore.bugReportsLoading = false;
  }
}

// ========== Feature Flags ==========

export async function fetchFeatureFlags(force = false) {
  // Cache-first: skip if already fetched (even if empty)
  if (!force && adminStore.featureFlagsFetched) return;

  adminStore.featureFlagsLoading = true;
  adminStore.error = null;

  try {
    const { data } = await db.queryOnce({
      feature_flags: {},
    });

    adminStore.featureFlags = (data?.feature_flags ?? []) as FeatureFlag[];
    adminStore.featureFlagsFetched = true;
  } catch (e) {
    adminStore.error =
      e instanceof Error ? e.message : "Failed to fetch feature flags";
  } finally {
    adminStore.featureFlagsLoading = false;
  }
}

// ========== Clear Error ==========

export function clearError() {
  adminStore.error = null;
}
